import { useState, useEffect } from "react";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import { ReceivableItem } from "../types/operations";

interface Props {
    receivables: ReceivableItem[];
    currentPage?: number;
    itemsPerPage?: number;
    onPageChange?: (page: number) => void;
    onRowClick?: (receivable: ReceivableItem) => void;
    onReceivablesUpdate?: (receivables: ReceivableItem[], spreadsheetProcessed: boolean, spreadsheetItemsCount: number) => void;
    spreadsheetProcessed?: boolean;
    onValidationChange?: (hasInvalidDueDates: boolean) => void;
    convertButton?: React.ReactNode;
}

const ReceivablesManager: React.FC<Props> = ({
    receivables,
    currentPage: externalCurrentPage,
    itemsPerPage = 25,
    onPageChange,
    onRowClick,
    onReceivablesUpdate,
    spreadsheetProcessed = false,
    onValidationChange,
    convertButton
}) => {
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const currentPage = externalCurrentPage ?? internalCurrentPage;
    const [missingInReceivables, setMissingInReceivables] = useState<Array<{nfe: string, parcela: number}>>([]);
    const [missingInSpreadsheet, setMissingInSpreadsheet] = useState<Array<{nfe: string, parcela: number}>>([]);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });

                // Verificar se existe a aba "Base Aprovados" (case insensitive)
                const sheetNames = workbook.SheetNames;
                const baseAprovadosName = sheetNames.find(
                    name => name.toLowerCase() === 'base aprovados'
                );

                if (!baseAprovadosName) {
                    toast.error('Planilha "Base Aprovados" não encontrada. Abas disponíveis: ' + sheetNames.join(', '));
                    return;
                }

                // Ler a planilha "Base Aprovados"
                const worksheet = workbook.Sheets[baseAprovadosName];

                // Usar sheet_to_json com defval para preservar colunas vazias
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: null,  // Preenche células vazias com null
                    raw: true      // Mantém valores originais sem formatação
                }) as any[][];

                if (jsonData.length === 0) {
                    toast.error('Planilha "Base Aprovados" está vazia');
                    return;
                }

                // Procurar linha de cabeçalho pelos valores, independente da coluna
                let headerRowIndex = -1;
                let colIndexNfe = -1;
                let colIndexParcela = -1;
                let colIndexDataOperacao = -1;
                let colIndexValorPresente = -1;
                let colIndexValorNF = -1;

                for (let i = 0; i < jsonData.length; i++) {
                    const row = jsonData[i];

                    // Procurar cada valor em qualquer coluna
                    for (let j = 0; j < row.length; j++) {
                        const cellValue = row[j]?.toString().trim() || '';

                        if (cellValue === 'Nfe') colIndexNfe = j;
                        if (cellValue === 'Parcela') colIndexParcela = j;
                        if (cellValue === 'Data Operação Agrolend') colIndexDataOperacao = j;
                        if (cellValue === 'Valor Presente') colIndexValorPresente = j;
                        if (cellValue === 'Valor NF') colIndexValorNF = j;
                    }

                    // Se encontrou todos os 5 valores, esta é a linha de cabeçalho
                    if (colIndexNfe !== -1 && colIndexParcela !== -1 &&
                        colIndexDataOperacao !== -1 && colIndexValorPresente !== -1 && colIndexValorNF !== -1) {
                        headerRowIndex = i;                        break;
                    }
                }

                if (headerRowIndex === -1) {
                    // Verificar qual coluna não foi encontrada
                    const missingColumns: string[] = [];

                    if (colIndexNfe === -1) missingColumns.push('"Nfe"');
                    if (colIndexParcela === -1) missingColumns.push('"Parcela"');
                    if (colIndexDataOperacao === -1) missingColumns.push('"Data Operação Agrolend"');
                    if (colIndexValorPresente === -1) missingColumns.push('"Valor Presente"');
                    if (colIndexValorNF === -1) missingColumns.push('"Valor NF"');

                    if (missingColumns.length > 0) {
                        toast.error(`Colunas não encontradas: ${missingColumns.join(', ')}`);
                    } else {
                        toast.error('Não foi possível encontrar os cabeçalhos esperados.');
                    }
                    return;
                }

                // Montar tabela interna com as colunas encontradas dinamicamente
                const internalTable = jsonData.slice(headerRowIndex + 1)
                    .map((row, index) => {
                        // Parar se linha estiver completamente vazia
                        const hasAnyValue = row.some(cell => cell !== null && cell !== undefined && cell !== '');
                        if (!hasAnyValue) {
                            return null; // Marca para parar
                        }

                        // Processar Data Operação: converter serial do Excel para dd/mm/yyyy
                        let dataOperacao = row[colIndexDataOperacao];
                        if (typeof dataOperacao === 'number') {
                            // Converter serial do Excel para data
                            const excelEpoch = new Date(1899, 11, 30);
                            const date = new Date(excelEpoch.getTime() + dataOperacao * 86400000);
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const year = date.getFullYear();
                            dataOperacao = `${day}/${month}/${year}`;
                        } else if (typeof dataOperacao === 'string') {
                            // Já é string, remover horas se existirem
                            dataOperacao = dataOperacao.split(' ')[0];
                        }

                        // Processar Valor Presente: remover R$ e converter para número
                        let valorPresente = row[colIndexValorPresente];
                        if (typeof valorPresente === 'string') {
                            // Remover R$, espaços, pontos (milhares) e substituir vírgula por ponto
                            valorPresente = valorPresente
                                .replace(/R\$\s*/g, '')
                                .replace(/\./g, '')
                                .replace(',', '.');
                            valorPresente = Number.parseFloat(valorPresente) || 0;
                        } else if (typeof valorPresente === 'number') {
                            // Já é número, manter como está
                        } else {
                            valorPresente = 0;
                        }
                        // Processar Valor NF
                        let valorNF = row[colIndexValorNF];
                        if (typeof valorNF === 'string') {
                            valorNF = valorNF
                                .replace(/R\$\s*/g, '')
                                .replace(/\./g, '')
                                .replace(',', '.');
                            valorNF = Number.parseFloat(valorNF) || 0;
                        } else if (typeof valorNF === 'number') {
                            // Já é número, manter como está
                        } else {
                            valorNF = 0;
                        }

                        return {
                            rowIndex: headerRowIndex + index + 2,
                            nfe: row[colIndexNfe],
                            parcela: row[colIndexParcela],
                            dataOperacao: dataOperacao,
                            valorPresente: valorPresente,
                            valorNF: valorNF
                        };
                    })
                    .filter((item, index, arr) => {
                        // Parar no primeiro null (linha vazia)
                        const firstNullIndex = arr.findIndex(item => item === null);
                        if (firstNullIndex === -1) return item !== null; // Sem linhas vazias
                        return index < firstNullIndex; // Manter apenas até primeira linha vazia
                    });

                // Validar formato de data (dd/mm/yyyy) na coluna Data Operação Agrolend
                const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                const invalidDates: string[] = [];

                internalTable.forEach((item) => {
                    if (!item) return;
                    
                    let dateValue = item.dataOperacao?.toString().trim() || '';

                    if (dateValue) {
                        // Remover horas se existirem (formato: dd/mm/yyyy hh:mm ou dd/mm/yyyy hh:mm:ss)
                        dateValue = dateValue.split(' ')[0];

                        // Atualizar valor na tabela interna
                        item.dataOperacao = dateValue;

                        // Verificar formato
                        if (!dateRegex.test(dateValue)) {
                            invalidDates.push(`Linha ${item.rowIndex}: "${dateValue}" (formato inválido)`);
                        } else {
                            // Verificar valores válidos (dia 1-31, mês 1-12)
                            const [day, month, year] = dateValue.split('/').map(Number);
                            if (day < 1 || day > 31) {
                                invalidDates.push(`Linha ${item.rowIndex}: "${dateValue}" (dia inválido: ${day})`);
                            }
                            if (month < 1 || month > 12) {
                                invalidDates.push(`Linha ${item.rowIndex}: "${dateValue}" (mês inválido: ${month})`);
                            }
                            if (year < 1900 || year > 2100) {
                                invalidDates.push(`Linha ${item.rowIndex}: "${dateValue}" (ano inválido: ${year})`);
                            }
                        }
                    }
                });

                if (invalidDates.length > 0) {
                    toast.error(`Datas inválidas encontradas (formato esperado: dd/mm/yyyy):\n${invalidDates.slice(0, 5).join('\n')}${invalidDates.length > 5 ? `\n... e mais ${invalidDates.length - 5}` : ''}`);
                    return;
                }

                // Atualizar recebíveis com os dados da planilha
                if (onReceivablesUpdate) {
                    const divergences: string[] = [];

                    const updatedReceivables = receivables.map(receivable => {
                        // Buscar correspondência por NFE e Parcela (normalizar parcela para comparação)
                        const receivableParcela = Number.parseInt(receivable.receivable_number || '0', 10);

                        const match = internalTable.find(row => {
                            if (!row) return false;
                            
                            const rowParcela = Number.parseInt(row.parcela?.toString() || '0', 10);
                            const nfeMatch = row.nfe?.toString().trim() === receivable.document_id?.toString().trim();
                            const parcelaMatch = rowParcela === receivableParcela;

                            if (nfeMatch) {                            }

                            return nfeMatch && parcelaMatch;
                        });

                        if (match) {
                            // Verificar divergência de valores (com tolerância de 0.01 para arredondamento)
                            const valorReceivable = receivable.receivable_amount || 0;
                            const valorPlanilha = match.valorNF || 0;
                            const hasDivergence = Math.abs(valorReceivable - valorPlanilha) > 0.01;

                            if (hasDivergence) {
                                const nfe = receivable.document_id || 'N/A';
                                divergences.push(`NFE ${nfe}: Recebível R$ ${valorReceivable.toFixed(2)} ≠ Planilha R$ ${valorPlanilha.toFixed(2)}`);                            }                            return {
                                ...receivable,
                                operation_date: match.dataOperacao,
                                present_value: match.valorPresente,
                                has_value_divergence: hasDivergence,
                                spreadsheet_nf_value: valorPlanilha
                            };
                        } else {                        }

                        return receivable;
                    });

                    const matchedCount = updatedReceivables.filter(d => d.operation_date || d.present_value).length;
                    // Verificar recebíveis na planilha mas não na lista
                    const nfesInSpreadsheet = internalTable.map(row => ({
                        nfe: row?.nfe?.toString().trim() || '',
                        parcela: Number.parseInt(row?.parcela?.toString() || '0', 10)
                    }));

                    const nfesInReceivables = receivables.map(dup => ({
                        nfe: dup.document_id?.toString().trim(),
                        parcela: Number.parseInt(dup.receivable_number || '0', 10)
                    }));

                    const missingInReceivablesList = nfesInSpreadsheet.filter(spreadItem => 
                        !nfesInReceivables.some(dupItem => 
                            dupItem.nfe === spreadItem.nfe && dupItem.parcela === spreadItem.parcela
                        )
                    );

                    const missingInSpreadsheetList = nfesInReceivables.filter(dupItem => 
                        !nfesInSpreadsheet.some(spreadItem => 
                            spreadItem.nfe === dupItem.nfe && spreadItem.parcela === dupItem.parcela
                        )
                    );

                    // Armazenar nos estados
                    setMissingInReceivables(missingInReceivablesList);
                    setMissingInSpreadsheet(missingInSpreadsheetList);

                    onReceivablesUpdate(updatedReceivables, true, internalTable.length);

                    // Exibir divergências de valores
                    if (divergences.length > 0) {
                        divergences.forEach(msg => toast.warning(msg, { autoClose: 8000 }));
                        toast.warning(`⚠️ ${divergences.length} divergência(s) de valor encontrada(s)!`, { autoClose: 5000 });
                    }

                    // Exibir itens faltantes na lista de recebíveis
                    if (missingInReceivablesList.length > 0) {
                        const missingList = missingInReceivablesList.slice(0, 5).map(item => 
                            `NFE ${item.nfe} - Parcela ${item.parcela}`
                        ).join(', ');
                        const moreText = missingInReceivablesList.length > 5 ? ` e mais ${missingInReceivablesList.length - 5}` : '';
                        toast.error(`⚠️ ${missingInReceivablesList.length} NFE(s) na planilha mas não na lista: ${missingList}${moreText}`, { autoClose: 10000 });
                    }

                    // Exibir itens faltantes na planilha
                    if (missingInSpreadsheetList.length > 0) {
                        const missingList = missingInSpreadsheetList.slice(0, 5).map(item => 
                            `NFE ${item.nfe} - Parcela ${item.parcela}`
                        ).join(', ');
                        const moreText = missingInSpreadsheetList.length > 5 ? ` e mais ${missingInSpreadsheetList.length - 5}` : '';
                        toast.error(`⚠️ ${missingInSpreadsheetList.length} NFE(s) na lista mas não na planilha: ${missingList}${moreText}`, { autoClose: 10000 });
                    }

                    toast.success(`Planilha processada! ${matchedCount} de ${receivables.length} recebíveis atualizados.`);
                } else {
                    console.warn('⚠️ onReceivablesUpdate não foi fornecido');
                    toast.success(`Planilha "Base Aprovados" carregada com sucesso! ${internalTable.length} linhas processadas.`);
                }

            } catch (error: unknown) {
                toast.error('Erro ao ler arquivo Excel');
                console.error(error);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handlePageChange = (newPage: number) => {
        if (onPageChange) {
            onPageChange(newPage);
        } else {
            setInternalCurrentPage(newPage);
        }
    };

    const totalPages = Math.ceil(receivables.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = receivables.slice(startIndex, endIndex);
    const totalAmount = receivables.reduce((sum, item) => sum + (item.receivable_amount || 0), 0);

    // Verificar recebíveis com data de vencimento em branco
    const receivablesWithoutDueDate = receivables.filter(item => 
        !item.receivable_due_date || item.receivable_due_date.trim() === ''
    );
    const hasInvalidDueDates = receivablesWithoutDueDate.length > 0;

    // Notificar componente pai sobre validação (usando useEffect para evitar atualização durante render)
    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(hasInvalidDueDates);
        }
    }, [hasInvalidDueDates, onValidationChange]);

    // Verificar erros se planilha foi processada
    const hasDivergences = spreadsheetProcessed && receivables.some(item => item.has_value_divergence);
    const hasErrors = spreadsheetProcessed && (hasDivergences || missingInReceivables.length > 0 || missingInSpreadsheet.length > 0);

    return (
        <div>
            <div className="flex items-center justify-between mt-4 mb-4">
                <h2 className="text-xl font-semibold dark:text-white">Recebíveis</h2>
                {convertButton}
            </div>

            {hasInvalidDueDates && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded">
                    <p className="text-sm text-red-800 dark:text-red-200 font-semibold mb-2">
                        ❌ {receivablesWithoutDueDate.length} recebível(is) sem data de vencimento
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                        Não é permitido submeter recebíveis com data de vencimento em branco.
                    </p>
                </div>
            )}

            {!spreadsheetProcessed && receivables.length > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                        ⚠️ Checagem de planilha não foi realizada
                    </p>
                </div>
            )}

            {hasErrors && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded">
                    <p className="text-sm text-red-800 dark:text-red-200 font-semibold mb-2">
                        ❌ Planilha processada mas contém erros
                    </p>
                    <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside">
                        {hasDivergences && (
                            <li>Divergências de valores entre recebíveis e planilha</li>
                        )}
                        {missingInReceivables.length > 0 && (
                            <li>
                                {missingInReceivables.length} NFE(s) na planilha mas não na lista: {' '}
                                {missingInReceivables.slice(0, 3).map(item => `${item.nfe}-${item.parcela}`).join(', ')}
                                {missingInReceivables.length > 3 && ` e mais ${missingInReceivables.length - 3}`}
                            </li>
                        )}
                        {missingInSpreadsheet.length > 0 && (
                            <li>
                                {missingInSpreadsheet.length} NFE(s) na lista mas não na planilha: {' '}
                                {missingInSpreadsheet.slice(0, 3).map(item => `${item.nfe}-${item.parcela}`).join(', ')}
                                {missingInSpreadsheet.length > 3 && ` e mais ${missingInSpreadsheet.length - 3}`}
                            </li>
                        )}
                    </ul>
                </div>
            )}

            <div className="mb-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Carregar arquivo Excel:
                    </label>
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        className="text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300 dark:hover:file:bg-blue-800"
                    />
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-300">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">#</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nome Sacado</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">CPF/CNPJ Sacado</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nome Cedente</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">NFE</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nº Título</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Valor documento</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Data Emissão</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Data Vencimento</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Parcela</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Data Operação Agrolend</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Valor Presente</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">UF</th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Cidade</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentItems.map((item, displayIndex) => {
                            const actualIndex = startIndex + displayIndex;
                            const hasNoDueDate = !item.receivable_due_date || item.receivable_due_date.trim() === '';
                            return (
                                <tr
                                    key={actualIndex}
                                    className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''} ${hasNoDueDate ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                                    onClick={() => onRowClick && onRowClick(item)}
                                >
                                    <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-600">{actualIndex + 1}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.debtor_name}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.debtor_cpf_cnpj}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.assignor_name}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.document_id}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.document_title}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">
                                        {item.receivable_amount
                                            ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.receivable_amount)
                                            : 'R$ 0,00'}
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">
                                        {item.issue_date ? item.issue_date.split('T')[0].split('-').reverse().join('/') : ''}
                                    </td>
                                    <td className={`px-2 py-2 whitespace-nowrap text-sm ${hasNoDueDate ? 'bg-red-100 dark:bg-red-900/30 font-semibold text-red-700 dark:text-red-300' : ''}`}>
                                        {item.receivable_due_date ? item.receivable_due_date.split('T')[0].split('-').reverse().join('/') : '⚠️ VAZIO'}
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.receivable_number}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.operation_date || '-'}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">
                                        {item.present_value
                                            ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.present_value)
                                            : '-'}
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.address_state}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-sm">{item.address_city}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 border border-t-0 border-gray-300 dark:border-gray-600 rounded-b px-2 py-2">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Total de recebíveis: {receivables.length}
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">
                    Soma dos recebíveis: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalAmount)}
                </div>
                {receivables.length > itemsPerPage && (
                    <div className="flex items-center justify-between mt-2">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            Mostrando {startIndex + 1} a {Math.min(endIndex, receivables.length)} de {receivables.length} recebíveis
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600"
                            >
                                Anterior
                            </button>
                            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                                Página {currentPage} de {totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReceivablesManager;
